<template>
  <div
    v-if="!closed"
    ref="el"
    :class="[
      $style.base,
      $style[size],
      $style[color],
      {
        [$style.smallScreen]: $screen === 's',
        [$style.closable]: closable,
        [$style.inline]: inline,
        [$style.alignVertical]:
          (closable && !secondaryAction) || (primaryAction && secondaryAction) || width < 330,
      }
    ]"
  >
    <div
      :class="$style.inner"
      v-test="'_base-alert'"
    >
      <div :class="$style.content">
        <div :class="$style.contentLeft">
          <span
            v-if="iconName"
            :class="$style.icon"
          >
            <BaseIcon
              :name="iconName"
              color="inherit"
              :size="size === 's' ? 's' : 'l'"
            />
          </span>
          {{ text }}
        </div>
        <div :class="$style.contentRight">
          <component
            :is="secondaryAction ? 'BaseButton' : 'BaseText'"
            v-if="
              primaryAction &&
              (typeof primaryAction === 'string' ||
                (primaryAction.text &&
                  (primaryAction.routerLink || primaryAction.href)))
            "
            :href="primaryAction.href || undefined"
            :routerLink="primaryAction.routerLink || undefined"
            :color="secondaryAction ? 'inverted' : color"
            @click="$emit('primaryAction')"
            v-test="'_base-alert-link-primary'"
          >
            {{ typeof primaryAction === 'string'
                ? primaryAction
                : primaryAction.text }}
          </component>
          <BaseText
            v-if="
              secondaryAction &&
              (typeof secondaryAction === 'string' ||
                (secondaryAction.text &&
                  (secondaryAction.routerLink || secondaryAction.href)))
            "
            :href="secondaryAction.href || undefined"
            :routerLink="secondaryAction.routerLink || undefined"
            :color="color"
            @click="$emit('secondaryAction')"
            v-test="'_base-alert-link-secondary'"
          >
            {{ typeof secondaryAction === 'string'
                ? secondaryAction
                : secondaryAction.text }}
          </BaseText>
        </div>
      </div>
      <div
        v-if="closable"
        :class="$style.close"
        @click="close"
      >
        <BaseIcon
          name="close"
          color="inherit"
          size="s"
        />
      </div>
    </div>
    <div :class="$style.background" />
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script
  setup
  lang="ts"
>
import { useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { LocationQueryRaw } from 'vue-router';
import type { IconName } from '@/components/base-icon/types';

type Link =
  | string
  | {
      href?: string;
      routerLink?: LocationQueryRaw;
      text: string;
    };

const props = withDefaults(
  defineProps<{
    closable?: boolean;
    color?: 'primary' | 'info' | 'success' | 'warning' | 'error';
    /** `true` shows the colour's filled status icon, `false` hides it, a name shows that icon instead */
    icon?: IconName | boolean;
    inline?: boolean;
    primaryAction?: Link;
    secondaryAction?: Link;
    size?: 's' | 'm';
    text: string;
  }>(),
  {
    closable: false,
    color: 'primary',
    icon: true,
    inline: false,
    size: 'm',
  },
);

const emit = defineEmits(['primaryAction', 'secondaryAction', 'close']);

const statusIcons: Record<NonNullable<typeof props.color>, IconName> = {
  primary: 'info-filled',
  info: 'info-filled',
  success: 'checkmark-circle-filled',
  warning: 'alert-triangle-filled',
  error: 'close-circle-filled',
};

const iconName = computed(() => {
  if (props.icon === true) {
    return statusIcons[props.color];
  }

  return props.icon || undefined;
});

const el = ref(null);
const { width } = useElementSize(el);

const closed = ref(false);
const close = () => {
  closed.value = true;
  emit('close');
};
</script>

<style
  lang="scss"
  module
>
$closeButtonSizeS: 28px;
$closeButtonSizeM: 34px;

.base {
  position: relative;
  border-radius: $radius;

  &.inline {
    display: inline-block;
  }

  // primary is the design system's neutral alert
  &.primary {
    color: var(--neutral-darker);
  }

  &.info {
    color: var(--info-darker);
  }

  &.success {
    color: var(--success-darker);
  }

  &.warning {
    color: var(--warning-darker);
  }

  &.error {
    color: var(--error-darker);
  }
}

.inner {
  position: relative;
  z-index: 1;

  .base.s & {
    font-size: 12px;
    padding: 5px $spacing * 0.5 6px;
  }

  .base.s.closable & {
    padding-right: $closeButtonSizeS;
  }

  .base.m & {
    padding: 13px;
  }

  .base.m.closable & {
    padding-right: $closeButtonSizeM;
  }
}

.background {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  border-radius: inherit;
  border: 1px solid;

  .base.primary & {
    border-color: var(--neutral-soft);
    background-color: var(--neutral-softer);
  }

  .base.info & {
    border-color: var(--info-soft);
    background-color: var(--info-softer);
  }

  .base.success & {
    border-color: var(--success-soft);
    background-color: var(--success-softer);
  }

  .base.warning & {
    border-color: var(--warning-soft);
    background-color: var(--warning-softer);
  }

  .base.error & {
    border-color: var(--error-soft);
    background-color: var(--error-softer);
  }
}

.icon {
  display: flex;
  flex-shrink: 0;

  .base.primary & {
    color: var(--neutral-base);
  }

  .base.info & {
    color: var(--info-base);
  }

  .base.success & {
    color: var(--success-base);
  }

  .base.warning & {
    color: var(--warning-base);
  }

  .base.error & {
    color: var(--error-base);
  }
}

.close {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);

  .base.s & {
    width: $closeButtonSizeS;
    height: $closeButtonSizeS;
  }

  .base.m & {
    width: $closeButtonSizeM;
    height: $closeButtonSizeM;
  }
}

.content {
  .base:not(.alignVertical) & {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.contentLeft {
  display: flex;
  align-items: center;

  .base.m & {
    gap: $spacing * 0.5;
  }

  .base.s & {
    gap: $spacing * 0.25;
  }
}

.contentRight {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing * 0.5 $spacing;

  &:empty {
    display: none;
  }

  .base:not(.alignVertical) & {
    flex-shrink: 0;
    margin-left: $spacing;
  }

  .base.alignVertical.s & {
    margin-top: $spacing * 0.5;
  }

  .base.alignVertical:not(.s) & {
    margin-top: $spacing;
  }
}
</style>
